import React, { useState, useEffect } from 'react';
import { getProducts, getProduct, updateProductLocation, updateProductPrice, updateProduct, deleteProductPhoto, reorderProductPhotos } from '../api';
import BarcodeScanner from '../components/BarcodeScanner';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

function Inventory({ user }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomPhotoIndex, setZoomPhotoIndex] = useState(0);

  // Edit form state
  const [editForm, setEditForm] = useState({
    barcode: '',
    description: '',
    variation: '',
    quantity: '',
    cost: '',
    store_price: '',
  });

  // Multi-photo state
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const filtered = products.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          (p.barcode && p.barcode.toLowerCase().includes(search)) ||
          (p.description && p.description.toLowerCase().includes(search)) ||
          (p.variation && p.variation.toLowerCase().includes(search)) ||
          (p.location && p.location.toLowerCase().includes(search))
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const handleScan = (barcode) => {
    setShowScanner(false);
    setSearchTerm(barcode);
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (err) {
      setError('Failed to load products');
    }
  };

  const viewProduct = async (productId, openEditLocation = false) => {
    try {
      const response = await getProduct(productId);
      const product = response.data;
      setSelectedProduct(product);
      setLocation(product.location || '');
      setPrice(product.price ? product.price.toString() : '');
      setEditingLocation(openEditLocation);
      setEditingPrice(false);
      setEditMode(false);
      setEditForm({
        barcode: product.barcode || '',
        description: product.description || '',
        variation: product.variation || '',
        quantity: product.quantity?.toString() || '0',
        cost: product.cost?.toString() || '',
        store_price: product.store_price?.toString() || '',
      });
      setExistingPhotos(product.photos || []);
      setNewPhotos([]);
      setPhotosToDelete([]);
      setZoomPhotoIndex(0);
    } catch (err) {
      setError('Failed to load product details');
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await updateProductLocation(selectedProduct.id, location);
      setMessage('Location updated successfully');
      setSelectedProduct({ ...selectedProduct, location });
      setEditingLocation(false);
      loadProducts();
    } catch (err) {
      setError('Failed to update location');
    }
  };

  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await updateProductPrice(selectedProduct.id, price);
      setMessage('Price updated successfully');
      setSelectedProduct({ ...selectedProduct, price: parseFloat(price) });
      setEditingPrice(false);
      loadProducts();
    } catch (err) {
      setError('Failed to update price');
    }
  };

  const closeProductView = () => {
    setSelectedProduct(null);
    setEditingLocation(false);
    setEditingPrice(false);
    setEditMode(false);
    setLocation('');
    setPrice('');
    setExistingPhotos([]);
    setNewPhotos([]);
    setPhotosToDelete([]);
  };

  // Multi-photo handlers
  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = existingPhotos.length - photosToDelete.length + newPhotos.length + files.length;

    if (totalPhotos > 5) {
      setError(`Maximum 5 photos allowed. You can add ${5 - (existingPhotos.length - photosToDelete.length + newPhotos.length)} more.`);
      return;
    }

    const newPhotoObjects = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setNewPhotos([...newPhotos, ...newPhotoObjects]);
    e.target.value = '';
  };

  const handleRemoveNewPhoto = (index) => {
    const updated = [...newPhotos];
    URL.revokeObjectURL(updated[index].preview);
    updated.splice(index, 1);
    setNewPhotos(updated);
  };

  const handleRemoveExistingPhoto = (photoId) => {
    setPhotosToDelete([...photosToDelete, photoId]);
  };

  const handleRestorePhoto = (photoId) => {
    setPhotosToDelete(photosToDelete.filter(id => id !== photoId));
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (index, isNew = false) => {
    setDraggedPhotoIndex({ index, isNew });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex, targetIsNew = false) => {
    if (draggedPhotoIndex === null) return;

    const { index: sourceIndex, isNew: sourceIsNew } = draggedPhotoIndex;

    // Only allow reordering within the same group (existing or new)
    if (sourceIsNew !== targetIsNew) {
      setDraggedPhotoIndex(null);
      return;
    }

    if (sourceIsNew) {
      const updated = [...newPhotos];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);
      setNewPhotos(updated);
    } else {
      const updated = [...existingPhotos];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);
      setExistingPhotos(updated);
    }

    setDraggedPhotoIndex(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      // First, delete photos marked for deletion
      for (const photoId of photosToDelete) {
        await deleteProductPhoto(selectedProduct.id, photoId);
      }

      // If existing photos were reordered, update the order
      const remainingPhotos = existingPhotos.filter(p => !photosToDelete.includes(p.id));
      if (remainingPhotos.length > 0) {
        const photoIds = remainingPhotos.map(p => p.id);
        await reorderProductPhotos(selectedProduct.id, photoIds);
      }

      // Build form data for product update
      const formData = new FormData();
      formData.append('barcode', editForm.barcode);
      formData.append('name', selectedProduct.name);
      formData.append('description', editForm.description);
      formData.append('variation', editForm.variation);
      formData.append('quantity', editForm.quantity);
      formData.append('cost', editForm.cost);
      formData.append('store_price', editForm.store_price);
      formData.append('location', location);
      if (price) {
        formData.append('price', price);
      }

      // Add new photos
      newPhotos.forEach(photo => {
        formData.append('photos', photo.file);
      });

      const response = await updateProduct(selectedProduct.id, formData);
      setMessage('Product updated successfully');
      setSelectedProduct(response.data);
      setExistingPhotos(response.data.photos || []);
      setNewPhotos([]);
      setPhotosToDelete([]);
      setEditMode(false);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update product');
    }
  };

  // Get all displayable photos for view mode
  const getDisplayPhotos = () => {
    return existingPhotos.map(p => p.photo_path);
  };

  const handlePrevPhoto = () => {
    const photos = getDisplayPhotos();
    setZoomPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextPhoto = () => {
    const photos = getDisplayPhotos();
    setZoomPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Inventory Management</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {showScanner ? (
          <BarcodeScanner
            onScan={handleScan}
            onClose={() => setShowScanner(false)}
          />
        ) : (
          <div className="form-group">
            <label>Search Products</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, barcode, description, variation, or location"
                  style={{ width: '100%', paddingRight: '35px' }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      padding: '5px',
                      lineHeight: 1
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowScanner(true)}
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
              >
                Scan
              </button>
            </div>
          </div>
        )}

        {!showScanner && selectedProduct ? (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={closeProductView}
              >
                Back to List
              </button>
              {user?.role === 'admin' && !editMode && (
                <button
                  className="btn btn-primary"
                  onClick={() => setEditMode(true)}
                >
                  Edit Product
                </button>
              )}
            </div>

            {editMode ? (
              /* Admin Edit Mode */
              <form onSubmit={handleSaveEdit}>
                <h3 style={{ marginBottom: '20px' }}>Edit: {selectedProduct.name}</h3>

                {/* Photo Section */}
                <div className="form-group">
                  <label>Product Photos ({existingPhotos.length - photosToDelete.length + newPhotos.length}/5)</label>

                  {/* Photo Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    {/* Existing Photos */}
                    {existingPhotos.map((photo, index) => {
                      const isDeleted = photosToDelete.includes(photo.id);
                      return (
                        <div
                          key={photo.id}
                          draggable={!isDeleted}
                          onDragStart={() => handleDragStart(index, false)}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDrop(index, false)}
                          style={{
                            position: 'relative',
                            aspectRatio: '1',
                            opacity: isDeleted ? 0.4 : 1,
                            cursor: isDeleted ? 'default' : 'grab',
                            border: index === 0 && !isDeleted ? '2px solid var(--color-primary)' : 'none',
                            borderRadius: '8px'
                          }}
                        >
                          <img
                            src={`${API_BASE}${photo.photo_path}`}
                            alt={`Product ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                          />
                          {index === 0 && !isDeleted && (
                            <span style={{
                              position: 'absolute',
                              top: '4px',
                              left: '4px',
                              backgroundColor: 'var(--color-primary)',
                              color: 'white',
                              fontSize: '0.65rem',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              Primary
                            </span>
                          )}
                          {isDeleted ? (
                            <button
                              type="button"
                              onClick={() => handleRestorePhoto(photo.id)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: 'var(--color-success)',
                                border: '2px solid white',
                                color: 'white',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              +
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingPhoto(photo.id)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: 'var(--color-error)',
                                border: '2px solid white',
                                color: 'white',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* New Photos (pending upload) */}
                    {newPhotos.map((photo, index) => (
                      <div
                        key={`new-${index}`}
                        draggable
                        onDragStart={() => handleDragStart(index, true)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(index, true)}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          cursor: 'grab',
                          border: '2px dashed var(--color-success)',
                          borderRadius: '8px'
                        }}
                      >
                        <img
                          src={photo.preview}
                          alt={`New ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '6px'
                          }}
                        />
                        <span style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          backgroundColor: 'var(--color-success)',
                          color: 'white',
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          New
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNewPhoto(index)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: 'var(--color-error)',
                            border: '2px solid white',
                            color: 'white',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add Photos Button */}
                  {(existingPhotos.length - photosToDelete.length + newPhotos.length) < 5 && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotosChange}
                        id="photo-upload"
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="photo-upload"
                        className="btn btn-secondary"
                        style={{ cursor: 'pointer', display: 'inline-block' }}
                      >
                        + Add Photos
                      </label>
                      <span style={{ marginLeft: '10px', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                        Drag to reorder. First photo is primary.
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Barcode</label>
                  <input
                    type="text"
                    value={editForm.barcode}
                    onChange={(e) => setEditForm({ ...editForm, barcode: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Variation</label>
                  <input
                    type="text"
                    value={editForm.variation}
                    onChange={(e) => setEditForm({ ...editForm, variation: e.target.value })}
                    placeholder="e.g., Size, Color"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Store Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.store_price}
                    onChange={(e) => setEditForm({ ...editForm, store_price: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Cost (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.cost}
                    onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Selling Price (PHP)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Shelf A-3"
                  />
                </div>

                <div className="btn-group" style={{ marginTop: '20px' }}>
                  <button type="submit" className="btn btn-success">
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditMode(false);
                      setExistingPhotos(selectedProduct.photos || []);
                      setNewPhotos([]);
                      setPhotosToDelete([]);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              /* View Mode */
              <>
                <div style={{ marginBottom: '20px' }}>
                  <h3>{selectedProduct.name}</h3>
                  {selectedProduct.variation && (
                    <p style={{ color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                      {selectedProduct.variation}
                    </p>
                  )}

                  {/* Photo Gallery */}
                  {existingPhotos.length > 0 ? (
                    <div style={{ marginBottom: '15px' }}>
                      {/* Main Photo */}
                      <img
                        src={`${API_BASE}${existingPhotos[zoomPhotoIndex]?.photo_path || existingPhotos[0]?.photo_path}`}
                        alt={selectedProduct.name}
                        onClick={() => setShowImageZoom(true)}
                        style={{
                          width: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          marginBottom: '10px',
                          cursor: 'pointer',
                          backgroundColor: 'var(--color-hudson)'
                        }}
                      />

                      {/* Thumbnail Strip */}
                      {existingPhotos.length > 1 && (
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          overflowX: 'auto',
                          paddingBottom: '5px'
                        }}>
                          {existingPhotos.map((photo, index) => (
                            <img
                              key={photo.id}
                              src={`${API_BASE}${photo.photo_path}`}
                              alt={`Thumbnail ${index + 1}`}
                              onClick={() => setZoomPhotoIndex(index)}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: index === zoomPhotoIndex ? '2px solid var(--color-primary)' : '2px solid transparent',
                                flexShrink: 0
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : user?.role === 'admin' && (
                    <div
                      style={{
                        width: '100%',
                        height: '150px',
                        backgroundColor: 'var(--color-hudson)',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      No photos - Click "Edit Product" to add
                    </div>
                  )}

                  {selectedProduct.description && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'var(--color-hudson)',
                      borderRadius: '5px',
                      marginBottom: '15px'
                    }}>
                      <strong>Description:</strong>
                      <p style={{ marginTop: '5px' }}>{selectedProduct.description}</p>
                    </div>
                  )}

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '15px',
                    marginBottom: '20px'
                  }}>
                    <div>
                      <strong>Quantity</strong>
                      <p style={{ fontSize: '1.2rem' }}>{selectedProduct.quantity}</p>
                    </div>
                    <div>
                      <strong>Selling Price</strong>
                      <p style={{ fontSize: '1.2rem', color: 'var(--color-success)' }}>
                        {selectedProduct.price ? `₱${parseFloat(selectedProduct.price).toFixed(2)}` : 'Not set'}
                      </p>
                    </div>

                    {/* Admin-only fields */}
                    {user?.role === 'admin' && (
                      <>
                        <div>
                          <strong>Cost</strong>
                          <p style={{ fontSize: '1.2rem' }}>
                            {selectedProduct.cost ? `$${parseFloat(selectedProduct.cost).toFixed(2)}` : 'Not set'}
                          </p>
                        </div>
                        <div>
                          <strong>Store Price</strong>
                          <p style={{ fontSize: '1.2rem' }}>
                            {selectedProduct.store_price ? `$${parseFloat(selectedProduct.store_price).toFixed(2)}` : 'Not set'}
                          </p>
                        </div>
                      </>
                    )}

                    <div>
                      <strong>Location</strong>
                      <p style={{ fontSize: '1.2rem' }}>{selectedProduct.location || 'Not set'}</p>
                    </div>
                    {selectedProduct.barcode && (
                      <div>
                        <strong>Barcode</strong>
                        <p style={{ fontSize: '1rem' }}>{selectedProduct.barcode}</p>
                      </div>
                    )}
                  </div>
                </div>

                {editingLocation ? (
                  <form onSubmit={handleUpdateLocation}>
                    <div className="form-group">
                      <label>New Location *</label>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g., Shelf A-3, Warehouse Section B"
                        required
                      />
                    </div>

                    <div className="btn-group">
                      <button type="submit" className="btn btn-success">
                        Save Location
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setEditingLocation(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => setEditingLocation(true)}
                  >
                    Set Location
                  </button>
                )}
              </>
            )}
          </>
        ) : !showScanner ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>QTY</th>
                  <th>Price</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr
                    key={product.id}
                    onClick={() => viewProduct(product.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {product.photo ? (
                          <img
                            src={`${API_BASE}${product.photo}`}
                            alt=""
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              flexShrink: 0
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: 'var(--color-hudson)',
                              borderRadius: '4px',
                              flexShrink: 0
                            }}
                          />
                        )}
                        <span>{product.name}</span>
                      </div>
                    </td>
                    <td>{product.quantity}</td>
                    <td>{product.price ? `₱${parseFloat(product.price).toFixed(2)}` : '-'}</td>
                    <td>{product.location || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* Image Zoom Modal */}
      {showImageZoom && existingPhotos.length > 0 && (
        <div
          onClick={() => setShowImageZoom(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Navigation Arrows */}
            {existingPhotos.length > 1 && (
              <>
                <button
                  onClick={handlePrevPhoto}
                  style={{
                    position: 'absolute',
                    left: '-50px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '2rem',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    borderRadius: '50%'
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={handleNextPhoto}
                  style={{
                    position: 'absolute',
                    right: '-50px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '2rem',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    borderRadius: '50%'
                  }}
                >
                  ›
                </button>
              </>
            )}

            <img
              src={`${API_BASE}${existingPhotos[zoomPhotoIndex]?.photo_path}`}
              alt={selectedProduct.name}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />

            {/* Photo Counter */}
            {existingPhotos.length > 1 && (
              <p style={{
                color: 'white',
                marginTop: '10px',
                fontSize: '0.9rem'
              }}>
                {zoomPhotoIndex + 1} / {existingPhotos.length}
              </p>
            )}

            <button
              onClick={() => setShowImageZoom(false)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '2rem',
                cursor: 'pointer',
                padding: '5px 10px'
              }}
            >
              ✕
            </button>
            <p style={{
              textAlign: 'center',
              color: 'white',
              marginTop: '5px',
              fontSize: '0.8rem'
            }}>
              Tap anywhere to close
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inventory;
